import React, { useMemo } from 'react';
import { X, MessageSquare, HelpCircle } from 'lucide-react';
import KaiPageLayout, { KAI_TAB_IDS } from '../components/ai/KaiPageLayout';
import KaiAskPanel from '../components/ai/KaiAskPanel';
import { AI_MODE } from '../constants/aiAssistant';
import { getKaiChatConfig, KAI_CHAT_CONFIGS } from '../constants/kaiChatConfig';
import { usePlatformKai } from './PlatformKaiContext';
import { createCopilotServiceForKey } from './shellKaiCopilotService';

const PLATFORM_TABS = [
  { id: KAI_TAB_IDS.ASK_KAI, label: 'Ask Kai', icon: MessageSquare },
  { id: 'support', label: 'Kai Support', icon: HelpCircle },
];

function RoutedKaiAsk({ serviceKey, onBeforeSend }) {
  const copilotService = useMemo(() => createCopilotServiceForKey(serviceKey), [serviceKey]);
  const config = getKaiChatConfig(serviceKey);

  // Spread the real service so conversation CRUD is carried through with the
  // shell's /rcm-api/<svc> base. Exposing only chat left the chat panel to
  // build its own client, which resolves to same-origin /api/v1 — proxied to
  // the SaaS backend in the shell, where /ai-copilot does not exist (404).
  const routedService = useMemo(
    () => ({
      ...copilotService,
      chat: async (message, userId, context, conversationId) => {
        // Awaited: routing consults the backend catalogue. Without the await
        // this resolved to a Promise, which is not a config key — every message
        // would fall back to the DenialManagement default.
        const routedKey = onBeforeSend ? await onBeforeSend(message) : serviceKey;
        const svc = createCopilotServiceForKey(routedKey);
        return svc.chat(
          message,
          userId,
          {
            ...context,
            platform_routed_from: serviceKey,
            platform_routed_to: routedKey,
          },
          conversationId
        );
      },
    }),
    [copilotService, onBeforeSend, serviceKey]
  );

  return (
    <KaiAskPanel
      key={serviceKey}
      serviceKey={serviceKey}
      copilotService={routedService}
      configOverride={{
        modelLabel: config.modelLabel,
        welcomeMessage: config.welcomeMessage,
      }}
    />
  );
}

const PlatformKaiPanel = () => {
  const {
    isOpen,
    closeKai,
    activeTab,
    setActiveTab,
    activeServiceKey,
    setManualServiceKey,
    routeMessageToSpecialist,
  } = usePlatformKai();

  if (!isOpen) return null;

  const config = getKaiChatConfig(activeServiceKey);
  const specialistOptions = Object.entries(KAI_CHAT_CONFIGS).filter(
    ([key]) => !['platform', 'kai_support'].includes(key)
  );

  const renderContent = (tab) => {
    if (tab === 'support') {
      return <RoutedKaiAsk serviceKey="kai_support" onBeforeSend={routeMessageToSpecialist} />;
    }
    return (
      <RoutedKaiAsk
        serviceKey={activeServiceKey}
        onBeforeSend={routeMessageToSpecialist}
      />
    );
  };

  return (
    <div className="fixed inset-0 z-[2000] flex justify-end bg-black/30">
      <button
        type="button"
        className="absolute inset-0 cursor-default"
        aria-label="Close Kai"
        onClick={closeKai}
      />
      <div className="relative w-full max-w-5xl h-full bg-gray-50 shadow-2xl flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b bg-white shrink-0 gap-3">
          <div className="min-w-0">
            <p className="text-sm font-semibold text-gray-900 truncate">Kai – AI Assistant</p>
            <p className="text-xs text-gray-500 truncate">
              Active specialist: {config.serviceName}
              {activeServiceKey === 'platform' ? ' (auto-routes by context)' : ''}
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <label className="hidden md:flex items-center gap-2 text-xs text-gray-600">
              Specialist
              <select
                className="text-sm border border-gray-200 rounded-md px-2 py-1 bg-white max-w-[180px]"
                value={activeServiceKey}
                onChange={(e) => setManualServiceKey(e.target.value)}
              >
                <option value="platform">Platform (auto)</option>
                {specialistOptions.map(([key, cfg]) => (
                  <option key={key} value={key}>
                    {cfg.serviceName}
                  </option>
                ))}
              </select>
            </label>
            <button
              type="button"
              onClick={closeKai}
              className="p-2 rounded-md text-gray-500 hover:text-gray-900 hover:bg-gray-100"
              aria-label="Close Kai panel"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          <KaiPageLayout
            serviceName={activeTab === 'support' ? 'Support' : config.serviceName}
            mode={AI_MODE.COWORKER}
            subtitle={
              activeTab === 'support'
                ? 'AI-based platform support, navigation, and troubleshooting'
                : 'Platform Kai routes to the right specialist based on where you are and what you ask'
            }
            tabs={PLATFORM_TABS}
            defaultTab={activeTab}
            renderContent={renderContent}
          />
        </div>
      </div>
    </div>
  );
};

export default PlatformKaiPanel;
